import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { api } from "../App";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Camera, Loader2, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_SIZE = 5 * 1024 * 1024;

/** Crop a square region of an image source URL and return a JPEG base64 data URL. */
async function getCroppedDataUrl(srcUrl, area, output = 600) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = output;
      canvas.height = output;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        img,
        area.x,
        area.y,
        area.width,
        area.height,
        0,
        0,
        output,
        output,
      );
      resolve(canvas.toDataURL("image/jpeg", 0.9));
    };
    img.onerror = (e) => reject(e);
    img.src = srcUrl;
  });
}

/**
 * ProfilePhotoCropper — modal flow:
 * 1) file picker (drag/drop optional)
 * 2) crop UI (square, zoom, rotate)
 * 3) preview + upload (calls POST /api/auth/profile-photo)
 *
 * Props:
 *  - open, onOpenChange (controlled dialog)
 *  - onUploaded(pictureUrl) — fired after successful upload
 *  - blocking — if true, X close is hidden (used by the post-login gate)
 */
export default function ProfilePhotoCropper({
  open,
  onOpenChange,
  onUploaded,
  blocking = false,
}) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);
  const [previewDataUrl, setPreviewDataUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const onCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedArea(croppedPixels);
  }, []);

  const reset = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedArea(null);
    setPreviewDataUrl(null);
    setUploading(false);
  };

  const handleClose = () => {
    if (blocking) return;
    reset();
    onOpenChange?.(false);
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error("Formato no soportado. Usa JPG, PNG o WebP.");
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("La imagen es demasiado grande (máximo 5 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setImageSrc(e.target?.result);
    reader.readAsDataURL(file);
  };

  const handlePreview = async () => {
    if (!imageSrc || !croppedArea) return;
    try {
      const dataUrl = await getCroppedDataUrl(imageSrc, croppedArea, 600);
      setPreviewDataUrl(dataUrl);
    } catch {
      toast.error("No se pudo procesar la imagen. Intenta con otra.");
    }
  };

  const handleUpload = async () => {
    if (!previewDataUrl) return;
    setUploading(true);
    try {
      const { data } = await api.post("/auth/profile-photo", {
        image_data: previewDataUrl,
      });
      toast.success("Foto de perfil guardada");
      onUploaded?.(data.picture);
      reset();
      onOpenChange?.(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo guardar la foto");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && blocking) return; // can't dismiss when blocking
        if (!o) reset();
        onOpenChange?.(o);
      }}
    >
      <DialogContent
        className={`max-w-md ${blocking ? "[&>button.absolute]:hidden" : ""}`}
        data-testid="profile-photo-cropper"
        onPointerDownOutside={(e) => blocking && e.preventDefault()}
        onEscapeKeyDown={(e) => blocking && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Foto de perfil</DialogTitle>
          <DialogDescription>
            {blocking
              ? "Para continuar usando Rivones, sube una foto de perfil clara y reciente."
              : "Sube una foto tuya clara, de frente. JPG, PNG o WebP — máximo 5 MB."}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: file pick */}
        {!imageSrc && (
          <label
            htmlFor="pp-file"
            className="block border-2 border-dashed border-neutral-300 rounded-2xl p-8 text-center cursor-pointer hover:border-terracotta hover:bg-terracotta/5 transition-colors"
            data-testid="profile-photo-dropzone"
          >
            <ImageIcon className="w-10 h-10 text-neutral-400 mx-auto mb-3" />
            <p className="font-medium text-agave">Selecciona una foto</p>
            <p className="text-xs text-neutral-500 mt-1">JPG · PNG · WebP · máx 5 MB</p>
            <input
              id="pp-file"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="user"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="profile-photo-file-input"
            />
          </label>
        )}

        {/* Step 2: crop */}
        {imageSrc && !previewDataUrl && (
          <div className="space-y-3" data-testid="profile-photo-crop-step">
            <div className="relative w-full h-64 bg-neutral-900 rounded-2xl overflow-hidden">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-neutral-500 w-12">Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-terracotta"
                data-testid="profile-photo-zoom"
              />
            </div>
          </div>
        )}

        {/* Step 3: preview */}
        {previewDataUrl && (
          <div className="text-center" data-testid="profile-photo-preview-step">
            <img
              src={previewDataUrl}
              alt="Vista previa"
              className="w-40 h-40 rounded-full mx-auto object-cover ring-4 ring-emerald-500/20"
              data-testid="profile-photo-preview-img"
            />
            <p className="text-sm text-neutral-600 mt-3">
              ¿Se ve bien? Verificaremos automáticamente que sea una foto tuya.
            </p>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {imageSrc && !uploading && (
            <Button variant="ghost" onClick={reset} disabled={uploading} data-testid="profile-photo-reset-btn">
              Cambiar foto
            </Button>
          )}
          {imageSrc && !previewDataUrl && (
            <Button
              onClick={handlePreview}
              disabled={!croppedArea}
              className="bg-[#111111] hover:bg-neutral-800 text-white"
              data-testid="profile-photo-preview-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Ver vista previa
            </Button>
          )}
          {previewDataUrl && (
            <>
              <Button
                variant="outline"
                onClick={() => setPreviewDataUrl(null)}
                disabled={uploading}
                data-testid="profile-photo-recrop-btn"
              >
                Volver a recortar
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="profile-photo-upload-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Guardar foto
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
